import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Divider,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useState, useEffect } from 'react';
import { API_GET, API_PATCH, DBRequest, useConfig } from '../../api/api';
import { useTranslation } from 'react-i18next';
import ExoAvatar from '../exo/ExoAvatar';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import TagIcon from '@mui/icons-material/Tag';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import MediaPreview from '../exo/explorer/MediaPreview';
import { ChatConversationSettings } from './ChatConversationSettings';
import { useChatClient } from './useChatClient';

export const ChatConversationInfo = ({ conversation, onClose, isMobile = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();
  const { updateConversation } = useChatClient();
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const isAdmin = conversation?.currentUserParticipant?.role === 'admin';
  const canEdit = isAdmin && conversation?.type !== 'direct';
  const isPinned = conversation?.currentUserParticipant?.isPinned;
  const isMuted = conversation?.currentUserParticipant?.mutedUntil && new Date(conversation.currentUserParticipant.mutedUntil) > new Date();

  useEffect(() => {
    if (!config || !conversation?.id) return;

    setIsLoading(true);
    DBRequest({
      config,
      path: `chat/conversations/${conversation.id}/files`,
      method: API_GET,
      onResponse: (res) => {
        setFiles(res.data || res || []);
        setIsLoading(false);
      },
      onError: () => setIsLoading(false),
    });
  }, [config, conversation?.id]);

  const handleTogglePin = () => {
    if (!config || !conversation) return;

    DBRequest({
      config,
      path: `chat/conversations/${conversation.id}/pin`,
      method: API_PATCH({}),
      onResponse: (res) => {
        const newIsPinned = res.data?.isPinned ?? res.isPinned ?? !isPinned;
        updateConversation(conversation.id, {
          currentUserParticipant: {
            ...conversation.currentUserParticipant,
            isPinned: newIsPinned,
          },
        });
      },
    });
  };

  const handleToggleMute = () => {
    if (!config || !conversation) return;

    // Toggle: if muted, unmute (duration=0), if not muted, mute for 1 year
    const duration = isMuted ? 0 : null;

    DBRequest({
      config,
      path: `chat/conversations/${conversation.id}/mute`,
      method: API_PATCH({ duration }),
      onResponse: (res) => {
        const newMutedUntil = res.data?.mutedUntil ?? res.mutedUntil ?? null;
        updateConversation(conversation.id, {
          currentUserParticipant: {
            ...conversation.currentUserParticipant,
            mutedUntil: newMutedUntil,
          },
        });
      },
    });
  };

  const getConversationIcon = () => {
    if (conversation.type === 'direct') {
      const otherParticipant = conversation.participants?.find(
        p => p.userId !== conversation.currentUserParticipant?.userId
      );
      // Picture is in user.picture (from UserResource via ChatParticipantResource)
      const pictureUrl = otherParticipant?.user?.picture?.temporaryUrl || otherParticipant?.user?.picture?.url;
      return (
        <ExoAvatar
          size={isMobile ? '80px' : '64px'}
          name={conversation.name || t('User')}
          picture={pictureUrl}
        />
      );
    }

    // Check for custom avatar for groups/channels
    const avatarUrl = conversation.avatar?.temporaryUrl || conversation.avatar?.url;
    if (avatarUrl) {
      return (
        <ExoAvatar
          size={isMobile ? '80px' : '64px'}
          name={conversation.name || t('Group')}
          picture={avatarUrl}
        />
      );
    }

    if (conversation.type === 'channel') {
      return (
        <Box
          className="flex items-center justify-center rounded-2xl"
          sx={{
            width: isMobile ? 80 : 64,
            height: isMobile ? 80 : 64,
            backgroundColor: colors.bgInput,
          }}
        >
          <TagIcon sx={{ color: colors.info, fontSize: isMobile ? 40 : 32 }} />
        </Box>
      );
    }

    return (
      <Box
        className="flex items-center justify-center rounded-2xl"
        sx={{
          width: isMobile ? 80 : 64,
          height: isMobile ? 80 : 64,
          backgroundColor: colors.bgInput,
        }}
      >
        <GroupIcon sx={{ color: colors.info, fontSize: isMobile ? 40 : 32 }} />
      </Box>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownload = (file, e) => {
    if (e) e.stopPropagation();
    const url = file.temporaryUrl || file.url;
    if (url) {
      // Create a link element for proper download
      const link = document.createElement('a');
      link.href = url;
      link.download = file.title || file.name || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };


  return (
    <Box
      className="flex flex-col h-full"
      sx={{
        width: isMobile ? '100%' : 320,
        borderLeft: isMobile ? 'none' : `1px solid ${colors.card}`,
        backgroundColor: isMobile ? 'transparent' : colors.glass,
      }}
    >
      {/* Header */}
      <Box className="flex items-center justify-between px-4 py-3">
        {isMobile ? (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: colors.text, opacity: 0.7, '&:hover': { opacity: 1 } }}
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
        ) : null}
        <Typography variant="body2" fontWeight={600} sx={{ color: colors.text, flex: 1, ml: isMobile ? 1 : 0 }}>
          {t('Info')}
        </Typography>
        <Box className="flex items-center gap-1">
          {canEdit && (
            <IconButton
              onClick={() => setShowSettings(true)}
              size="small"
              sx={{ color: colors.text, opacity: 0.5, '&:hover': { opacity: 1 } }}
            >
              <SettingsIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
          {!isMobile && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ color: colors.text, opacity: 0.5, '&:hover': { opacity: 1 } }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Divider sx={{ borderColor: colors.card }} />

      {/* Conversation Info */}
      <Box className="flex flex-col items-center py-6 px-4">
        {getConversationIcon()}
        <Typography
          variant={isMobile ? 'h5' : 'h6'}
          fontWeight={600}
          sx={{ color: colors.text, mt: 2, textAlign: 'center' }}
        >
          {conversation.name || t('Chat')}
        </Typography>
        {conversation.description && (
          <Typography
            variant="body2"
            sx={{ color: colors.text, opacity: 0.6, mt: 1, textAlign: 'center', px: isMobile ? 2 : 0 }}
          >
            {conversation.description}
          </Typography>
        )}

        {/* Pin & Mute Actions */}
        <Box className="flex items-center gap-2 mt-4">
          <IconButton
            onClick={handleTogglePin}
            size="small"
            sx={{
              color: isPinned ? colors.info : colors.text,
              backgroundColor: isPinned ? `${colors.info}15` : colors.bgInput,
              '&:hover': { backgroundColor: isPinned ? `${colors.info}25` : colors.card },
            }}
            title={isPinned ? t('Unpin') : t('Pin')}
          >
            {isPinned ? <PushPinIcon sx={{ fontSize: 20, transform: 'rotate(45deg)' }} /> : <PushPinOutlinedIcon sx={{ fontSize: 20 }} />}
          </IconButton>
          <IconButton
            onClick={handleToggleMute}
            size="small"
            sx={{
              color: isMuted ? colors.warning : colors.text,
              backgroundColor: isMuted ? `${colors.warning}15` : colors.bgInput,
              '&:hover': { backgroundColor: isMuted ? `${colors.warning}25` : colors.card },
            }}
            title={isMuted ? t('Unmute') : t('Mute')}
          >
            {isMuted ? <VolumeOffIcon sx={{ fontSize: 20 }} /> : <VolumeUpIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ borderColor: colors.card }} />

      {/* Participants */}
      <Box className="px-4 py-3">
        <Typography variant="caption" fontWeight={600} sx={{ color: colors.text, opacity: 0.6, textTransform: 'uppercase' }}>
          {t('Participants')} ({conversation.participants?.length || 0})
        </Typography>
      </Box>

      <List sx={{ px: 1, py: 0, maxHeight: isMobile ? 250 : 200, overflow: 'auto' }}>
        {conversation.participants?.map((participant) => {
          const user = participant.user || participant;
          return (
            <ListItemButton
              key={participant.id || user.id}
              sx={{
                borderRadius: '10px',
                py: isMobile ? 1.5 : 1,
                '&:hover': { backgroundColor: colors.card },
              }}
            >
              <ListItemAvatar sx={{ minWidth: isMobile ? 52 : 44 }}>
                <ExoAvatar
                  size={isMobile ? '40px' : '32px'}
                  name={`${user.firstName || ''} ${user.lastName || ''}`}
                  picture={user.picture?.temporaryUrl || user.picture?.url}
                />
              </ListItemAvatar>
              <ListItemText
                primary={`${user.firstName || ''} ${user.lastName || ''}`}
                secondary={participant.role === 'admin' ? t('Admin') : null}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption', sx: { color: colors.info } }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ borderColor: colors.card, mt: 2 }} />

      {/* Shared Files */}
      <Box className="px-4 py-3">
        <Typography variant="caption" fontWeight={600} sx={{ color: colors.text, opacity: 0.6, textTransform: 'uppercase' }}>
          {t('Shared Files')} ({files.length})
        </Typography>
      </Box>

      <Box className="flex-1 overflow-auto px-1">
        {isLoading ? (
          <Box className="flex justify-center py-4">
            <CircularProgress size={24} sx={{ color: colors.info }} />
          </Box>
        ) : files.length === 0 ? (
          <Typography variant="body2" sx={{ color: colors.text, opacity: 0.4, textAlign: 'center', py: 4 }}>
            {t('No files shared yet')}
          </Typography>
        ) : (
          <List sx={{ py: 0 }}>
            {files.map((file) => (
              <ListItemButton
                key={file.id}
                sx={{
                  borderRadius: '10px',
                  py: isMobile ? 1.5 : 1,
                  '&:hover': { backgroundColor: colors.card },
                }}
              >
                <ListItemAvatar sx={{ minWidth: isMobile ? 60 : 52 }}>
                  <MediaPreview
                    size={isMobile ? '44px' : '36px'}
                    src={file.temporaryUrl || file.thumbnailUrl || file.url}
                    mimeType={file.mimeType || 'application/octet-stream'}
                    allowPreview={true}
                    allowDownload={false}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={file.title || file.name || t('File')}
                  secondary={formatFileSize(file.fileSize || file.size || 0)}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500, noWrap: true }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.5 } }}
                />
                <IconButton
                  size="small"
                  onClick={(e) => handleDownload(file, e)}
                  sx={{ color: colors.text, opacity: 0.4, '&:hover': { opacity: 1 } }}
                >
                  <DownloadIcon sx={{ fontSize: isMobile ? 22 : 18 }} />
                </IconButton>
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>

      {/* Settings Dialog */}
      <ChatConversationSettings
        open={showSettings}
        onClose={() => setShowSettings(false)}
        conversation={conversation}
      />
    </Box>
  );
};
